import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowRight, Zap } from 'lucide-react';
import BuzzerHost from './BuzzerHost';
import BuzzerPlayer, { PlayerBuzzState } from './BuzzerPlayer';
import { BuzzerMode, useBuzzerSignalR } from '../../../hooks/useBuzzerSignalR';
import { BUZZER_MODE_LABELS } from '../../../const/buzzer';

type Role = 'host' | 'player' | null;

const sameName = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const Buzzer: React.FC = () => {
  const {
    error,
    players,
    presses,
    isReset,
    hostConnected,
    mode,
    lockedOutPlayers,
    createGame,
    joinGame,
    pressButton,
    resetGame,
    resetRound,
    setBuzzerMode,
    stopConnection
  } = useBuzzerSignalR();

  const [role, setRole] = useState<Role>(null);
  const [activeCode, setActiveCode] = useState('');
  const [activeName, setActiveName] = useState('');
  const [hostKey, setHostKey] = useState('');

  // Join form state
  const [joinName, setJoinName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // True between sending a press and the server echoing it back.
  const [pressPending, setPressPending] = useState(false);

  // Effect to handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Any reset (or mode switch) starts a fresh round for this player.
  useEffect(() => {
    if (isReset) {
      setPressPending(false);
    }
  }, [isReset]);

  // ── Derived player state ───────────────────────────────────────────────────
  const myRank = useMemo(() => {
    if (!activeName) return null;
    return presses.find(p => sameName(p.playerName, activeName))?.order ?? null;
  }, [presses, activeName]);

  const isBlocked = useMemo(
    () => Boolean(activeName) && lockedOutPlayers.some(name => sameName(name, activeName)),
    [lockedOutPlayers, activeName]
  );

  const buzzState: PlayerBuzzState = myRank !== null
    ? 'pressed'
    : isBlocked
      ? 'blocked'
      : mode === 'lockout' && presses.length > 0
        ? 'locked'
        : 'ready';

  const playerStatus = useMemo(() => {
    switch (buzzState) {
      case 'pressed':
        return mode === 'lockout' ? 'Düymə səndədir — cavab ver!' : `Sən #${myRank} sıxdın!`;
      case 'blocked':
        return 'Bu sualdan kənardasan. Moderator tam sıfırlayana qədər gözlə.';
      case 'locked':
        return `${presses[0]?.playerName ?? 'Başqa oyunçu'} birinci sıxdı.`;
      default:
        return 'Hazır ol — düyməni sıx!';
    }
  }, [buzzState, mode, myRank, presses]);

  const handleCreateHostGame = async () => {
    setIsCreating(true);
    const created = await createGame();
    setIsCreating(false);
    
    if (created) {
      setActiveCode(created.code);
      setHostKey(created.hostKey);
      setRole('host');
      toast.success('Oyun uğurla yaradıldı!');
    }
  };

  const handleJoinGame = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!joinName.trim() || !joinCode.trim()) {
      toast.warning('Ad və kodu daxil edin');
      return;
    }

    setIsJoining(true);
    // The server owns the canonical name (it trims and caps the length).
    const registeredName = await joinGame(joinCode, joinName.trim());
    setIsJoining(false);

    if (registeredName) {
      setActiveCode(joinCode);
      setActiveName(registeredName);
      setRole('player');
      setPressPending(false);
      toast.success('Oyuna qoşuldunuz!');
    }
  };

  const handlePressBuzz = async () => {
    if (role !== 'player' || buzzState !== 'ready' || pressPending) return;

    setPressPending(true);
    await pressButton(activeCode);
    setPressPending(false);
  };

  /** `silent` skips the toast — used when the point counter resets the buzzer itself. */
  const handleResetAll = async (silent = false) => {
    if (role !== 'host') return;
    const ok = await resetGame(activeCode, hostKey);
    if (ok && !silent) {
      toast.info(mode === 'lockout' ? 'Oyun hamı üçün sıfırlandı' : 'Nəticələr sıfırlandı');
    }
  };

  const handleResetOthers = async (silent = false) => {
    if (role !== 'host') return;
    const ok = await resetRound(activeCode, hostKey);
    if (ok && !silent) {
      toast.info('Düyməni sıxanlar kənarda qaldı, digərləri yenidən sıxa bilər');
    }
  };

  const handleModeChange = async (nextMode: BuzzerMode) => {
    if (role !== 'host' || nextMode === mode) return;
    const ok = await setBuzzerMode(activeCode, hostKey, nextMode);
    if (ok) {
      toast.info(`${BUZZER_MODE_LABELS[nextMode].title} rejimi aktivdir`);
    }
  };

  const handleLeave = () => {
    stopConnection();
    setRole(null);
    setActiveCode('');
    setActiveName('');
    setHostKey('');
    setJoinName('');
    setJoinCode('');
    setPressPending(false);
  };

  // Rendering logic
  if (role === 'host') {
    return (
      <BuzzerHost
        gameCode={activeCode}
        players={players}
        presses={presses}
        lockedOutPlayers={lockedOutPlayers}
        mode={mode}
        hostConnected={hostConnected}
        onModeChange={handleModeChange}
        onResetAll={handleResetAll}
        onResetOthers={handleResetOthers}
        onLeave={handleLeave}
      />
    );
  }

  if (role === 'player') {
    return (
      <BuzzerPlayer
        gameCode={activeCode}
        playerName={activeName}
        mode={mode}
        buzzState={buzzState}
        myRank={myRank}
        pressPending={pressPending}
        statusText={playerStatus}
        onPress={handlePressBuzz}
        onLeave={handleLeave}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-24 pb-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 mt-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-3 bg-blue-100/50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl mb-4">
            <Zap className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-black text-gray-900 dark:text-gray-50 tracking-tight">Düymə sistemi</h1>
          <p className="mt-3 text-lg text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
            Moderator olaraq oyun yaradın və ya oyunçu kimi mövcud oyuna qoşulun.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* Join Game Card */}
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 dark:border-gray-800">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-6">Oyuna qoşul</h2>
            <form onSubmit={handleJoinGame} className="space-y-4">
              <div>
                <label htmlFor="playerName" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">Adınız</label>
                <input
                  id="playerName"
                  type="text"
                  value={joinName}
                  onChange={(e) => setJoinName(e.target.value)}
                  placeholder="Məs: Əli"
                  className="block w-full rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-4 py-3 text-gray-900 dark:text-gray-50 focus:border-blue-500 focus:bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-200 transition-colors"
                  required
                />
              </div>
              <div>
                <label htmlFor="gameCode" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">6 Rəqəmli Kod</label>
                <input
                  id="gameCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="block w-full rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-4 py-3 text-gray-900 dark:text-gray-50 font-mono text-lg tracking-[0.2em] focus:border-blue-500 focus:bg-white dark:bg-gray-900 focus:ring-2 focus:ring-blue-200 transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isJoining || joinCode.length !== 6 || !joinName.trim()}
                className="w-full flex items-center justify-center gap-2 mt-6 rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-bold text-white transition-all hover:bg-blue-700 hover:shadow-md hover:shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isJoining ? 'Qoşulur...' : 'Qoşul'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Create Game Card */}
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950 dark:to-blue-950 border border-blue-100 dark:border-blue-900 rounded-3xl p-6 sm:p-8 shadow-sm flex flex-col justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-4">Moderator</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Yeni buzzer otağı yaradın və kodu oyunçularla paylaşaraq onları otağa cəlb edin.
              </p>
            </div>
            
            <button
              onClick={handleCreateHostGame}
              disabled={isCreating}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-4 py-3.5 text-sm font-bold text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all shadow-sm disabled:opacity-50"
            >
              {isCreating ? 'Yaradılır...' : 'Yeni otaq yarat'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Buzzer;
